import React, { useContext, useState } from 'react';
import { get } from 'lodash';
import FilterSVG from 'src/assets/svg/filter-outline.svg';
import { Button, Select, toast } from 'src/components';
import { useAccount } from 'src/queries/account';
import { useCompany } from 'src/queries/company';
import { useTree } from 'src/queries/tree';
import { GoalsContext } from 'src/containers/Goals/context/GoalsProvider';
import { dateKeys } from 'src/containers/Goals/GoalsList';
import { exportGoalsQuery } from 'src/containers/Goals/queries';
import FilterByDate, {
  LABEL_AS
} from 'src/containers/UserProfile/FilterView/FilterByDate';
import FiltersFlyout from 'src/containers/UserProfile/FilterView/FiltersFlyout';
import GeneralFilter from 'src/containers/UserProfile/FilterView/GeneralFilter';
import COMMON_CONSTANTS from 'common/commonConstants';
import commonDateUtils from 'common/commonDateUtils';
import commonPermissions from 'common/commonPermissions';
import commonTreeUtils from 'common/commonTreeUtils';

const { GOALS, ACCESS, DATE_RANGE_FILTERS } = COMMON_CONSTANTS;

const TopFilters = ({ showActions = true }) => {
  const [userSearchText, setUserSearchText] = useState('');
  const { context, updateContext, resetContext } = useContext(GoalsContext);
  const { mutateAsync: exportData } = exportGoalsQuery();
  const {
    data: company,
    isFetching: isFetchingCompany,
    isError: isErrorCompany
  } = useCompany();
  const {
    data: { myTreeRow, tree, managerList },
    isFetching: isFetchingTree,
    isError: isErrorTree
  } = useTree();
  const {
    data: account,
    isFetching: isFetchingAccount,
    isError: isErrorAccount
  } = useAccount('me');

  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);
  const [flyoutContext, setFlyoutContext] = useState(context);
  const toggleFlyout = () => setIsFlyoutOpen(!isFlyoutOpen);

  const isFetching = isFetchingCompany || isFetchingAccount || isFetchingTree;
  const isError = isErrorCompany || isErrorAccount || isErrorTree;
  const isReady = company
    && company.id
    && account
    && account._id
    && myTreeRow
    && myTreeRow.id
    && !isFetching
    && !isError;

  const syncContext = (newValues = {}) => {
    const newContext = {
      ...context,
      ...flyoutContext,
      ...newValues
    };

    if (!isFlyoutOpen) {
      updateContext(newContext);
    }
    setFlyoutContext(newContext);
  };

  if (!isReady) {
    return null;
  }

  const {
    status, assignees, groupId, levels, dateKey, teamId
  } = isFlyoutOpen
    ? flyoutContext
    : context;

  const isViewerAdmin = commonPermissions.isAdmin(account);
  const myTreeList = account.access === ACCESS.ADMIN
    ? managerList
    : commonTreeUtils
      .getManagerNodeUnder(tree, myTreeRow.id)
      .filter((user) => user.id !== myTreeRow.id);

  const teamOptions = isViewerAdmin
    ? [
      { id: null, label: 'No team filter' },
      ...myTreeList.map((node) => ({
        id: node.id,
        label: node.name
      }))
    ]
    : [
      { id: null, label: account.name },
      ...myTreeList.map((node) => ({
        id: node.id,
        label: node.name
      }))
    ];

  const getActionsOptions = () => [{ id: 'excel', label: 'Export' }];
  let filterByTeamTitle = 'Filter by team';
  if (teamId !== undefined && tree) {
    if (teamId === null) {
      filterByTeamTitle = teamOptions[0].label;
    } else filterByTeamTitle = commonTreeUtils.findNodeById(tree, teamId).name;
  }

  let filterByStatusTitle = 'Filter by status';

  if (status && status.length) {
    if (status.length === 1) {
      filterByStatusTitle = '1 status selected';
    } else {
      filterByStatusTitle = `${status.length} statuses selected`;
    }
  }

  const updateSelectedStatus = (key) => {
    let newStatuses = status ? [...status] : [];
    const exists = newStatuses && newStatuses.some((value) => value === key);
    if (exists) {
      newStatuses = newStatuses.filter((value) => value !== key);
    } else {
      newStatuses.push(key);
    }

    syncContext({
      status: newStatuses
    });
  };

  const statusOptions = [
    ...Object.keys(GOALS.STATUS).map((key) => ({
      id: GOALS.STATUS[key],
      label: GOALS.STATUS[key],
      checked: status && status.some((s) => s === GOALS.STATUS[key])
    }))
  ];

  const updateSelectedUsers = (userId) => {
    let newAssignees = assignees ? [...assignees] : [];
    const exists = newAssignees && newAssignees.some((id) => id === userId);
    if (exists) {
      newAssignees = newAssignees.filter((id) => id !== userId);
    } else {
      newAssignees.push(userId);
    }

    syncContext({
      teamId: null,
      groupId: null,
      assignees: newAssignees
    });
  };

  const filterByUserTitle = assignees && assignees.length
    ? `${assignees.length} ${
      assignees.length === 1 ? 'user' : 'users'
    } selected`
    : 'Filter by users';
  let users = myTreeRow.access === ACCESS.ADMIN
    ? commonTreeUtils.convertTreeToList(tree, { byReference: false })
    : commonTreeUtils.convertTreeToList(myTreeRow, { byReference: false });

  users = users
    .filter((u) => u.id !== myTreeRow.id)
    .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));
  const userOptions = [
    {
      // stringified null so it won't be ignored by the request
      id: 'null',
      label: 'Unassigned',
      checked: assignees && assignees.some((id) => id === '')
    },
    {
      id: myTreeRow.id,
      label: myTreeRow.name,
      checked: assignees && assignees.some((id) => id === myTreeRow.id)
    },
    ...users.map((u) => ({
      id: u.id,
      label: u.name,
      checked: assignees && assignees.some((id) => id === u.id)
    }))
  ].filter((u) => {
    if (!userSearchText) {
      return true;
    }
    return u.label.toLowerCase().includes(userSearchText.toLowerCase());
  });

  const { groups: companyGroups = [] } = company;
  const filterByGroupTitle = groupId && companyGroups.length
    ? companyGroups.find((g) => g.id === groupId).name
    : 'Filter by group';
  const groupOptions = [
    { id: null, label: 'No group filter' },
    ...companyGroups.map((g) => ({
      id: g.id,
      label: g.name
    }))
  ];

  const filterByLevelTitle = levels && levels.length
    ? `${levels.length} ${levels.length === 1 ? 'type' : 'types'} selected`
    : 'Filter by type';

  const updateSelectedLevels = (key) => {
    let newLevels = levels ? [...levels] : [];
    const exists = newLevels && newLevels.some((value) => value === key);
    if (exists) {
      newLevels = newLevels.filter((value) => value !== key);
    } else {
      newLevels.push(key);
    }

    syncContext({
      levels: newLevels
    });
  };

  const levelOptions = Object.keys(GOALS.LEVELS)
    .filter((key) => GOALS.LEVELS[key] !== GOALS.LEVELS.ALL)
    .map((key) => {
      const val = GOALS.LEVELS[key];
      return {
        id: val,
        label: val,
        checked: levels && levels.some((s) => s === val)
      };
    });

  const exportFn = async () => {
    const res = await exportData({
      filename: 'Goals',
      filters: { ...context }
    });
    toast.show('Export successful');
  };

  const getDefaultDateRange = (key) => {
    if (key === 'deadline') {
      return {
        value: DATE_RANGE_FILTERS.THREE_MONTHS.key,
        start: commonDateUtils.getUnixDateNow(),
        end: commonDateUtils.getUnixDateFromNow(
          DATE_RANGE_FILTERS.THREE_MONTHS.days
        )
      };
    }

    return {
      value: DATE_RANGE_FILTERS.THREE_MONTHS.key,
      start: commonDateUtils.getUnixDateFromDaysAgo(
        DATE_RANGE_FILTERS.THREE_MONTHS.days
      ),
      end: commonDateUtils.getUnixDateNow()
    };
  };

  const dateKeyOptions = Object.values(dateKeys);

  const getFilterByDateKeyTitle = () => {
    const option = dateKeyOptions.find((o) => o.id === dateKey);

    if (option) {
      return `Filter by ${option.label}`;
    }
  };

  const applyFilters = () => {
    updateContext({
      ...context,
      ...flyoutContext
    });
    setIsFlyoutOpen(false);
  };

  const clearFilters = () => {
    const initialContext = resetContext();
    setFlyoutContext(initialContext);
    setIsFlyoutOpen(false);
  };

  const setRange = (value, start, end) => {
    const startValue = commonDateUtils.getFirstUnixOfDateFromUnix(
      start || commonDateUtils.dateToUnix('2020-01-01')
    );
    const endValue = commonDateUtils.getLastUnixOfDateFromUnix(
      end || commonDateUtils.dateToUnix(new Date())
    );

    const isCustom = value === DATE_RANGE_FILTERS.CUSTOM.key;

    syncContext({
      [dateKey]: {
        value,
        start: startValue,
        end: isCustom ? endValue : undefined
      }
    });
  };

  const renderLocalFilters = (isFlyout = false) => {
    const wrapperClasses = isFlyout ? 'w-full' : 'w-48';

    const dateRangeSelected = isFlyout
      ? flyoutContext[flyoutContext.dateKey]
      : context[context.dateKey];

    let dateFilterPrefix = null;
    if (
      get(dateRangeSelected, 'value', null) === DATE_RANGE_FILTERS.ALL_TIME.key
    ) {
      dateFilterPrefix = '';
    } else if (dateKey === 'deadline') {
      dateFilterPrefix = 'Within';
    } else {
      dateFilterPrefix = 'Last';
    }

    return (
      <>
        <GeneralFilter
          label={isFlyout ? 'Team' : ''}
          wrapperClasses={wrapperClasses}
          title={filterByTeamTitle}
          options={teamOptions}
          onChange={(option) => syncContext({
            teamId: option.id,
            assignees: [],
            groupId: null
          })}
        />
        <GeneralFilter
          label={isFlyout ? 'Status' : ''}
          wrapperClasses={wrapperClasses}
          multiselect
          title={filterByStatusTitle}
          options={statusOptions}
          onChange={(option) => updateSelectedStatus(option.id)}
          onClear={() => {
            syncContext({ status: [] });
          }}
        />
        <GeneralFilter
          label={isFlyout ? 'Date' : ''}
          wrapperClasses={wrapperClasses}
          title={getFilterByDateKeyTitle()}
          options={dateKeyOptions}
          onChange={(option) => {
            const dateKeyIds = dateKeyOptions.map((o) => o.id);
            const dateFilters = {
              dateKey: option.id,
              sort: {
                field: option.id,
                order: 'desc'
              }
            };

            for (const key of dateKeyIds) {
              if (key === option.id) {
                dateFilters[key] = getDefaultDateRange(option.id);
              } else {
                dateFilters[key] = undefined;
              }
            }

            syncContext(dateFilters);
          }}
        />
        <FilterByDate
          dateRangeSelected={dateRangeSelected}
          onSelectDateRange={setRange}
          classes={wrapperClasses}
          prefix={dateFilterPrefix}
          labelAs={LABEL_AS.TIME}
        />
      </>
    );
  };

  const renderFlyoutFilters = () => {
    const wrapperClasses = 'w-full';

    return (
      <>
        {renderLocalFilters(true)}
        <GeneralFilter
          label='Assignee'
          drop='up'
          wrapperClasses={wrapperClasses}
          multiselect
          title={filterByUserTitle}
          options={userOptions}
          onChange={(option) => updateSelectedUsers(option.id)}
          showSearch
          onSearch={(value) => {
            setUserSearchText(value);
          }}
          onDropdownClose={() => {
            setUserSearchText('');
          }}
          onClear={() => {
            syncContext({ assignees: [] });
            setUserSearchText('');
          }}
        />
        <GeneralFilter
          label='Group'
          drop='up'
          wrapperClasses={wrapperClasses}
          title={filterByGroupTitle}
          options={groupOptions}
          onChange={(option) => syncContext({
            groupId: option.id,
            assignees: [],
            teamId: null
          })}
        />
        <GeneralFilter
          label='Level'
          drop='up'
          wrapperClasses={wrapperClasses}
          multiselect
          title={filterByLevelTitle}
          options={levelOptions}
          onChange={(option) => updateSelectedLevels(option.id)}
          onClear={() => {
            syncContext({ levels: [] });
          }}
        />
      </>
    );
  };

  return (
    <div className='flex flex-col gap-2 mt-1 mb-2'>
      <FiltersFlyout
        isOpen={isFlyoutOpen}
        setIsOpen={setIsFlyoutOpen}
        render={renderFlyoutFilters}
        apply={applyFilters}
        clear={clearFilters}
        onClose={() => {
          setFlyoutContext(context);
        }}
      />
      <div className='w-60'>
        {showActions ? (
          <Select
            id='top-filters-actions-filter'
            title='Actions'
            options={getActionsOptions()}
            onChange={() => {
              exportFn();
            }}
          />
        ) : null}
      </div>
      <div className='flex justify-between w-full'>
        <div className='flex flex-wrap full:flex-nowrap justify-start gap-2 w-full'>
          {renderLocalFilters()}
        </div>
        <div className='flex items-center justify-between gap-0 m-0 p-0'>
          <Button
            variant='custom'
            classes='flex items-center justify-center flex-row gap-2 text-center bg-white font-bold text-sm px-2 min-w-[2.32rem] h-[2.32rem] ring-1 ring-inset ring-gray-300 border-0 rounded-md shadow-sm'
            onClick={toggleFlyout}
          >
            <FilterSVG />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TopFilters;
